import type { EnergyAnalysisTrendSeries } from "features/energy-analysis-dashboard/model/types";

import { legendsConfigAtBottom } from "components/chart/DynamicChartMemoized";
import { formatNumberBasedOnUser } from "utils/namesManipulation";
import { getCSSVar } from "utils/styling";

import type {
  CrremPathwayOverlayLine,
  CrremPathwayPartitionBy,
  CrremPathwayScatterSeries,
  CrremYearlyPoint,
} from "../model/types";

import { CRREM_PATHWAY_PARTITION_BY } from "../model/constants";
import { buildCorrectedBarSeries } from "./buildCorrectedBarSeries";
import { valueAtYear } from "./collectPathwayYears";
import { toSafeChartSeriesName } from "./formatScenarioLabel";

type BuildPathwayChartOptionArgs = {
  baseline: CrremYearlyPoint[];
  labels: PathwayChartLabels;
  overlayLines: CrremPathwayOverlayLine[];
  partitionBy: CrremPathwayPartitionBy;
  pathway: CrremYearlyPoint[];
  scatterSeries?: CrremPathwayScatterSeries[];
  stacks: EnergyAnalysisTrendSeries[];
  unit: string;
  xAxisType?: "category" | "value";
  years: number[];
};

type PathwayChartLabels = {
  baseline: string;
  pathway: string;
  year: string;
};

const readTooltipValue = (data: null | number | number[] | undefined) => {
  if (Array.isArray(data)) return data[1];
  return data;
};

const nearestCategory = (years: number[], year: number) =>
  years.reduce((best, item) => (Math.abs(item - year) < Math.abs(best - year) ? item : best), years[0]);

const inYearRange = (year: number, years: number[]) =>
  years.length === 0 || (year >= years[0] && year <= years[years.length - 1]);

const yearlyLineData = (points: CrremYearlyPoint[], years: number[], asCoordinates: boolean) =>
  years.map((year) => {
    const value = valueAtYear(points, year);
    return asCoordinates ? [year, value] : value;
  });

const staircaseLineData = (
  points: CrremYearlyPoint[],
  years: number[],
  asCoordinates: boolean,
  disconnectBetweenYears = false
) => {
  const filtered = points.filter((point) => inYearRange(point.year, years));
  if (!asCoordinates) return years.map((year) => valueAtYear(filtered, year));
  if (!disconnectBetweenYears) return filtered.map((point) => [point.year, point.value]);
  return filtered.flatMap((point, index) => {
    const coordinate = [point.year, point.value];
    return index > 0 && point.year !== filtered[index - 1].year ? [null, coordinate] : [coordinate];
  });
};

export const buildPathwayChartOption = ({
  baseline,
  labels,
  overlayLines,
  partitionBy,
  pathway,
  scatterSeries = [],
  stacks,
  unit,
  xAxisType,
  years,
}: BuildPathwayChartOptionArgs) => {
  const asLine = partitionBy === CRREM_PATHWAY_PARTITION_BY.none;
  const useValueXAxis = xAxisType === "value" || (asLine && overlayLines.some((line) => line.keepYearDuplicates));
  return {
    animation: false,
    dataZoom: [{ right: 0, type: "slider", width: 16, yAxisIndex: 0 }],
    grid: { ...legendsConfigAtBottom.grid, bottom: 85, top: 10 },
    legend: {
      ...legendsConfigAtBottom.legend,
      hoverLink: false,
      textStyle: {
        ...legendsConfigAtBottom.legend.textStyle,
        fontSize: "1.1rem",
      },
      type: undefined,
    },
    series: [
      ...buildCorrectedBarSeries(stacks, asLine).map((series) => ({
        ...series,
        data: useValueXAxis && asLine ? years.map((year, index) => [year, series.data[index] ?? null]) : series.data,
      })),
      ...overlayLines.map((line, index) => ({
        blur: { lineStyle: { opacity: 1, type: "dashed", width: 2 } },
        color: line.color,
        connectNulls: line.connectNulls ?? true,
        data: line.keepYearDuplicates
          ? staircaseLineData(line.points, years, useValueXAxis, line.connectNulls === false)
          : yearlyLineData(line.points, years, useValueXAxis),
        emphasis: { disabled: true },
        id: line.id ? `overlay-${line.id}` : `overlay-${index}-${toSafeChartSeriesName(line.name)}`,
        itemStyle: { color: line.color },
        legendHoverLink: false,
        lineStyle: { color: line.color, type: line.lineType ?? "dashed", width: 2 },
        name: toSafeChartSeriesName(line.name),
        symbol: line.symbol ?? "none",
        symbolSize: line.symbol === "circle" ? 6 : 0,
        type: "line",
        z: 3,
      })),
      ...scatterSeries.map((series, seriesIndex) => ({
        data: series.points.map((point) =>
          useValueXAxis ? [point.x, point.y] : [String(nearestCategory(years, point.x)), point.y]
        ),
        id: `stranding-${seriesIndex}`,
        itemStyle: {
          borderColor: getCSSVar("--color-purpose-modernization-charts-stranding-event"),
          borderWidth: 3,
          color: "none",
          shadowBlur: 4,
          shadowColor: getCSSVar("--color-purpose-modernization-charts-stranding-event-shadow"),
        },
        name: series.name,
        symbolSize: 16,
        type: "scatter" as const,
        z: 4,
      })),
      {
        blur: { lineStyle: { opacity: 1, type: "dashed", width: 2 } },
        color: getCSSVar("--color-purpose-modernization-charts-baseline-emission"),
        connectNulls: true,
        data: yearlyLineData(baseline, years, useValueXAxis),
        emphasis: { disabled: true },
        id: "baseline",
        legendHoverLink: false,
        lineStyle: {
          color: getCSSVar("--color-purpose-modernization-charts-baseline-emission"),
          type: "dashed",
          width: 2,
        },
        name: labels.baseline,
        symbol: "none",
        type: "line",
        z: 3,
      },
      {
        areaStyle: { color: getCSSVar("--color-purpose-modernization-charts-decarbonization-target"), opacity: 0.3 },
        blur: { areaStyle: { opacity: 0.3 }, lineStyle: { opacity: 1, width: 2 } },
        color: getCSSVar("--color-purpose-modernization-charts-decarbonization-target"),
        connectNulls: true,
        data: yearlyLineData(pathway, years, useValueXAxis),
        emphasis: { disabled: true },
        id: "pathway",
        legendHoverLink: false,
        lineStyle: { color: getCSSVar("--color-purpose-modernization-charts-decarbonization-target"), width: 2 },
        name: labels.pathway,
        symbol: "none",
        type: "line",
        z: 3,
      },
    ],
    tooltip: {
      formatter: (
        params: { axisValue?: number | string; data?: number | number[]; marker: string; seriesName: string }[]
      ) => {
        const year = params[0]?.axisValue;
        const lines = params
          .filter((item) => {
            const value = readTooltipValue(item.data);
            return value !== null && value !== undefined && value !== 0 && item.seriesName !== "Retrofit";
          })
          .map((item) => `${item.marker} ${item.seriesName}: ${formatNumberBasedOnUser(readTooltipValue(item.data))}`)
          .join("<br/>");
        return `<h1>${year ?? ""}</h1>${lines}`;
      },
      trigger: "axis",
    },
    xAxis: [
      useValueXAxis
        ? {
            axisPointer: { type: "shadow" },
            max: years[years.length - 1],
            min: years[0],
            name: labels.year,
            nameGap: 25,
            nameLocation: "center",
            type: "value" as const,
          }
        : {
            axisPointer: { type: "shadow" },
            boundaryGap: !asLine,
            data: years,
            name: labels.year,
            nameGap: 25,
            nameLocation: "center",
            type: "category" as const,
          },
    ],
    yAxis: [{ min: 0, name: unit, scale: false, type: "value" }],
  };
};
